import { format, isToday, isTomorrow } from "date-fns";

import { datetimeToJS } from "@/helpers/parsers";
import { cn } from "@/lib/utils";
import type { SearchGameType } from "@/pages/search/types";

function formatDateText(date: Date, i: number, tomorrowIdx: number) {
  const day = format(date, "E, MMM d");
  const time = format(date, "h:mm");

  if (isToday(date) && i === 0) {
    return `Today at ${time}`;
  }
  if (isToday(date)) {
    return time;
  }
  if (isTomorrow(date) && i === tomorrowIdx) {
    return `Tomorrow at ${time}`;
  }
  if (isTomorrow(date) && i > tomorrowIdx) {
    return time;
  }
  return `${day} at ${time}`;
}

interface TimeBadgeProps {
  as?: string;
  children: React.ReactNode;
  className?: string;
}

function TimeBadge({ as = "div", children, className }: TimeBadgeProps) {
  const Component = as as React.ElementType;

  return (
    <Component
      className={cn(
        "rounded-[3px] bg-white/5 px-0.5 py-[3px] md:py-1.5",
        className
      )}
    >
      <p className="whitespace-nowrap text-11 text-white/80 md:text-12">
        {children}
      </p>
    </Component>
  );
}

interface AvailableTimesProps {
  className?: string;
  game: SearchGameType;
}

export default function AvailableTimes({
  className,
  game,
}: AvailableTimesProps) {
  // filter out times that are not available
  const times = game?.timeslots
    .filter((i) => i.available)
    .map((i) => i.datetime);

  if (!times.length) {
    return null;
  }

  // we find the first time that is tomorrow and log the index for formatting
  const firstTomorrowIdx = times.findIndex((date) =>
    isTomorrow(datetimeToJS(date))
  );

  const dates = times.map((date, i) => {
    const parsed = datetimeToJS(date);
    const text = formatDateText(parsed, i, firstTomorrowIdx);
    const ampm = format(parsed, "aaa");

    return (
      <TimeBadge as="li" key={date}>
        {text}{" "}
        <small className="text-11 text-battleship-gray md:text-12">
          {ampm}
        </small>
      </TimeBadge>
    );
  });

  return (
    <div className={cn("flex", className)}>
      <ul className="mask-linear-90 mask-linear-from-black mask-linear-from-90% mask-linear-to-transparent flex gap-1 overflow-hidden">
        {dates}
      </ul>
      <TimeBadge className="shrink-0 bg-transparent">more...</TimeBadge>
    </div>
  );
}
